import { useId } from "react";
import type { Currency } from "@retirement-planner/contract";

/**
 * Currency-aware money input. Value is integer minor units (satang/cents), never a
 * float. The user edits major units; we convert to minor units on change.
 */
export function MoneyField({
  label,
  amountMinorUnits,
  currency,
  onChange,
  error,
  min = 0,
}: {
  label: string;
  amountMinorUnits: number;
  currency: Currency;
  onChange: (minorUnits: number) => void;
  error?: string;
  min?: number;
}) {
  const id = useId();
  const errId = `${id}-err`;
  const major = amountMinorUnits / 100;
  return (
    <div className="field" data-invalid={error ? "true" : undefined}>
      <label htmlFor={id}>{label}</label>
      <div className="field__control">
        <input
          id={id}
          className="tabular"
          inputMode="decimal"
          type="number"
          min={min}
          step="1"
          placeholder="0"
          value={Number.isFinite(major) && major !== 0 ? major : ""}
          aria-invalid={error ? "true" : undefined}
          aria-describedby={error ? errId : undefined}
          onChange={(e) => {
            const v = Number(e.target.value);
            onChange(Number.isFinite(v) ? Math.round(v * 100) : 0);
          }}
        />
        <span className="field__suffix">{currency}</span>
      </div>
      {error && (
        <span className="field__error" id={errId}>
          {error}
        </span>
      )}
    </div>
  );
}

/** Whole-number percent input (6.0 == 6%). */
export function PercentField({
  label,
  value,
  onChange,
  error,
}: {
  label: string;
  value: number;
  onChange: (pct: number) => void;
  error?: string;
}) {
  const id = useId();
  const errId = `${id}-err`;
  return (
    <div className="field" data-invalid={error ? "true" : undefined}>
      <label htmlFor={id}>{label}</label>
      <div className="field__control">
        <input
          id={id}
          className="tabular"
          inputMode="decimal"
          type="number"
          step="0.1"
          value={value}
          aria-invalid={error ? "true" : undefined}
          aria-describedby={error ? errId : undefined}
          onChange={(e) => onChange(Number(e.target.value))}
        />
        <span className="field__suffix">%</span>
      </div>
      {error && (
        <span className="field__error" id={errId}>
          {error}
        </span>
      )}
    </div>
  );
}
